import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Activity, ChevronDown, ArrowLeft, Download } from 'lucide-react';
import { getDrills, getGPS, getPlayers, getPlayerPositions } from '../../api';
import { MATCH_TARGETS, findMatchPosition, parseTimeToMinutes, f0, f1, fPct } from './matchChronicShared';
import { downloadBlob, renderSinglePagePdf } from '../../pdfExport';

// Fixed light colors for the hidden export table below — deliberately NOT this page's
// var(--text-primary)/var(--bg-card) etc. Those resolve to whatever the live app's CURRENT
// theme is (default dark: near-white text), and the PDF is always captured onto a forced white
// background — dark-mode text would render invisible. Same fixed-light-palette convention
// PhysicalReportContent.js/MatchChronicPrint.js already use for exportable content; the green
// here matches their --text-on-green day-theme value (#065f46) exactly.
const EX = { bg: '#ffffff', subtle: '#f8fafc', border: '#e2e8f0', text: '#0f172a', muted: '#64748b', dimmed: '#94a3b8' };
const exTh = { padding: '7px 8px', background: EX.subtle, color: EX.muted, fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', textAlign: 'right', borderBottom: `1px solid ${EX.border}`, whiteSpace: 'nowrap' };
const exThLeft = { ...exTh, textAlign: 'left' };
const exTd = { padding: '6px 8px', fontSize: 10, color: EX.text, textAlign: 'right', borderBottom: `1px solid ${EX.subtle}`, whiteSpace: 'nowrap' };
const exTdL = { ...exTd, textAlign: 'left' };
const exGreen = { background: 'rgba(16,185,129,0.15)', color: '#065f46' };

const POSITIONS = ['All', 'CB', 'FB', 'CDM', 'CAM', 'ST', 'W'];
const TARGET_KEYS = [
  { key: 'td',   targetKey: 'TD',   label: 'TD' },
  { key: 'hid',  targetKey: 'HID',  label: 'HID >19.8' },
  { key: 'sd',   targetKey: 'SD',   label: 'SD >25.2' },
  { key: 'hmld', targetKey: 'HMLD', label: 'HMLD' },
  { key: 'sa',   targetKey: 'SA',   label: 'SA' },
];

const inputStyle = { padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' };

function todayStr() { return new Date().toISOString().slice(0, 10); }
function daysAgoStr(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

const WIDE_FROM = '2020-01-01';

// Averages a %-of-target field across rows, skipping rows with no value (no target/no minutes)
// instead of counting them as 0 — same convention matchPerformanceShared.js's avgPct uses.
const avgPct = (arr, key) => {
  const vals = arr.map(r => r[key]).filter(v => v != null && !isNaN(v));
  return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
};

// Collapses several selected-drill rows for the same player+session into one combined row —
// same "combine selected drills" idea as MetricsTable.js's aggregateGroupRows, trimmed to just
// the fields this page compares against match targets.
function aggregateGroupRows(groupRows) {
  const first = groupRows[0];
  const sum = k => groupRows.reduce((s, r) => s + (r[k] || 0), 0);
  return {
    id: `combined:${first.sessionId}:${first.playerId}`,
    sessionId: first.sessionId,
    date: first.date,
    sessionTitle: first.sessionTitle,
    drillTitle: groupRows.map(r => r.drillTitle).join(' + '),
    playerId: first.playerId,
    playerName: first.playerName,
    position: first.position,
    td: sum('td'), hid: sum('hid'), sd: sum('sd'), hmld: sum('hmld'), sa: sum('sa'),
    maxSpeed: Math.max(...groupRows.map(r => r.maxSpeed || 0)),
    mins: sum('mins'),
  };
}

export default function DrillPerformance({ user = null, onNavigate = null }) {
  const [dateFrom, setDateFrom] = useState(() => daysAgoStr(7));
  const [dateTo, setDateTo] = useState(() => todayStr());
  const [selectedSessionDates, setSelectedSessionDates] = useState(new Set());
  const [datesPickerOpen, setDatesPickerOpen] = useState(false);
  const [drillRows, setDrillRows] = useState([]);
  const [sessionsById, setSessionsById] = useState({});
  const [playersById, setPlayersById] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDrills, setSelectedDrills] = useState(new Set());
  const [drillPickerOpen, setDrillPickerOpen] = useState(false);
  // Defaults on for this page (unlike MetricsTable's off-by-default) — the whole point here is
  // one total-vs-target analysis for "the drill", which usually means several logged segments
  // (halves/quarters) of the same session that should read as one combined outing.
  const [combineSelectedDrills, setCombineSelectedDrills] = useState(true);
  const [posFilter, setPosFilter] = useState('All');
  // Raises the bar players are judged against — 100% is the plain MATCH_TARGETS table, 120%
  // demands 20% more than a full match's worth (pro-rated for minutes played same as always).
  const [targetPct, setTargetPct] = useState(100);
  const [sortKey, setSortKey] = useState('hmld');
  const [sortDir, setSortDir] = useState('desc');
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);

  const handleDateFromChange = v => { setDateFrom(v); setSelectedSessionDates(new Set()); };
  const handleDateToChange = v => { setDateTo(v); setSelectedSessionDates(new Set()); };
  const toggleSessionDate = date => setSelectedSessionDates(prev => {
    const next = new Set(prev);
    next.has(date) ? next.delete(date) : next.add(date);
    return next;
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([getDrills({ from: WIDE_FROM, to: todayStr() }), getGPS(), getPlayers(), getPlayerPositions()])
      .then(([drills, sessions, players, posHistory]) => {
        if (cancelled) return;
        setDrillRows(drills || []);
        const byId = {};
        (sessions || []).forEach(s => { byId[s.id] = { date: s.session_date?.slice(0, 10), title: s.session_title }; });
        setSessionsById(byId);
        const byPlayer = {};
        (players || []).forEach(p => { byPlayer[String(p.id)] = { id: p.id, active: p.active !== false, position: p.position || '' }; });
        setPlayersById(byPlayer);
        setPositions((posHistory || []).map(p => ({ ...p, startDate: p.start_date?.slice(0, 10), playerId: p.player_id })));
      })
      .catch(err => { console.error('Failed to load Drill Performance data:', err); if (!cancelled) setError('Failed to load GPS data.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // One row per player per drill, joined with its session's date/title, position resolved as of
  // that session's date (same date-scoped lookup Match Performance uses for match day).
  const rows = useMemo(() => {
    return drillRows
      .filter(d => !playersById || playersById[String(d.player_id)]?.active)
      .map(d => {
        const session = sessionsById[d.session_id] || {};
        const player = playersById?.[String(d.player_id)];
        return {
          id: d.id,
          sessionId: d.session_id,
          date: session.date || '',
          sessionTitle: session.title || '',
          drillTitle: d.drill_title || '',
          playerId: d.player_id,
          playerName: d.player_name || 'Unknown',
          position: findMatchPosition(positions, d.player_id, session.date, player?.position),
          td: parseFloat(d.td) || 0, hid: parseFloat(d.hid) || 0, sd: parseFloat(d.sd) || 0,
          hmld: parseFloat(d.hmld) || 0, sa: parseFloat(d.sa) || 0,
          maxSpeed: parseFloat(d.max_speed) || 0,
          mins: parseTimeToMinutes(d.total_time),
        };
      });
  }, [drillRows, sessionsById, playersById, positions]);

  const dateFilteredRows = useMemo(() => {
    return rows.filter(r => {
      if (selectedSessionDates.size) return selectedSessionDates.has(r.date);
      if (dateFrom && r.date < dateFrom) return false;
      if (dateTo && r.date > dateTo) return false;
      return true;
    });
  }, [rows, selectedSessionDates, dateFrom, dateTo]);

  const drillTitles = useMemo(() => [...new Set(dateFilteredRows.map(r => r.drillTitle).filter(Boolean))].sort(), [dateFilteredRows]);

  const sessionDateOptions = useMemo(() => {
    const titlesByDate = {};
    Object.values(sessionsById).forEach(s => {
      if (!s.date) return;
      if (!titlesByDate[s.date]) titlesByDate[s.date] = new Set();
      if (s.title) titlesByDate[s.date].add(s.title);
    });
    return Object.keys(titlesByDate).sort().reverse().map(date => {
      const [y, m, d] = date.split('-');
      const titles = [...titlesByDate[date]].join(', ');
      return { date, label: `${Number(d)}-${m}-${y}${titles ? ' ' + titles : ''}` };
    });
  }, [sessionsById]);

  // A selected drill that no longer occurs in the active date filter is dropped rather than
  // silently filtering everything to zero rows.
  useEffect(() => {
    if (!selectedDrills.size) return;
    const stillValid = [...selectedDrills].filter(t => drillTitles.includes(t));
    if (stillValid.length !== selectedDrills.size) setSelectedDrills(new Set(stillValid));
  }, [drillTitles, selectedDrills]);

  useEffect(() => {
    if (combineSelectedDrills && selectedDrills.size < 2) setCombineSelectedDrills(false);
  }, [combineSelectedDrills, selectedDrills]);

  const toggleDrill = title => setSelectedDrills(prev => {
    const next = new Set(prev);
    next.has(title) ? next.delete(title) : next.add(title);
    return next;
  });

  const filteredRows = useMemo(() => {
    return dateFilteredRows.filter(r => selectedDrills.size === 0 || selectedDrills.has(r.drillTitle));
  }, [dateFilteredRows, selectedDrills]);

  const combineDrills = combineSelectedDrills && selectedDrills.size >= 2;

  const displayRows = useMemo(() => {
    if (!combineDrills) return filteredRows;
    const groups = new Map();
    filteredRows.forEach(r => {
      const key = `${r.sessionId}:${r.playerId}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    });
    return [...groups.values()].map(aggregateGroupRows);
  }, [filteredRows, combineDrills]);

  // Attach %-of-target and green flags — targets pro-rate DOWN for minutes played (capped at a
  // full 90) same as Match Performance, then scaled by the target-percentage slider.
  const scoredRows = useMemo(() => {
    const multiplier = targetPct / 100;
    return displayRows.map(r => {
      const tgt = r.position ? MATCH_TARGETS[r.position] : null;
      const targetScale = Math.min(r.mins, 90) / 90;
      const row = { ...r };
      TARGET_KEYS.forEach(({ key, targetKey }) => {
        const base = tgt?.[targetKey];
        const effectiveTarget = base && r.mins > 0 ? base * multiplier * targetScale : null;
        const pct = effectiveTarget ? (r[key] / effectiveTarget) * 100 : null;
        row[`${key}Pct`] = pct;
        row[`${key}Green`] = pct != null && Math.round(pct) >= 100;
      });
      return row;
    });
  }, [displayRows, targetPct]);

  const filteredScoredRows = useMemo(
    () => posFilter === 'All' ? scoredRows : scoredRows.filter(r => r.position === posFilter),
    [scoredRows, posFilter]
  );

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filteredScoredRows].sort((a, b) => {
      if (sortKey === 'playerName') return (a.playerName || '').localeCompare(b.playerName || '') * dir;
      const va = a[sortKey]; const vb = b[sortKey];
      if (typeof va === 'string' || typeof vb === 'string') return String(va ?? '').localeCompare(String(vb ?? '')) * dir;
      return ((va ?? -Infinity) - (vb ?? -Infinity)) * dir;
    });
  }, [filteredScoredRows, sortKey, sortDir]);

  const totals = useMemo(() => ({
    td: filteredScoredRows.reduce((s, r) => s + r.td, 0),
    hid: filteredScoredRows.reduce((s, r) => s + r.hid, 0),
    sd: filteredScoredRows.reduce((s, r) => s + r.sd, 0),
    hmld: filteredScoredRows.reduce((s, r) => s + r.hmld, 0),
    sa: filteredScoredRows.reduce((s, r) => s + r.sa, 0),
    maxSpeed: filteredScoredRows.length ? Math.max(...filteredScoredRows.map(r => r.maxSpeed)) : 0,
    mins: filteredScoredRows.reduce((s, r) => s + r.mins, 0),
    tdPct: avgPct(filteredScoredRows, 'tdPct'),
    hidPct: avgPct(filteredScoredRows, 'hidPct'),
    sdPct: avgPct(filteredScoredRows, 'sdPct'),
    hmldPct: avgPct(filteredScoredRows, 'hmldPct'),
    saPct: avgPct(filteredScoredRows, 'saPct'),
  }), [filteredScoredRows]);

  const handleSort = key => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const exportSubtitle = [
    selectedDrills.size ? [...selectedDrills].join(', ') : 'All drills',
    selectedSessionDates.size ? `${selectedSessionDates.size} selected date(s)` : `${dateFrom} to ${dateTo}`,
    posFilter === 'All' ? 'All positions' : posFilter,
    `Target level: ${targetPct}%`,
  ].join('  ·  ');

  const handleExportPdf = async () => {
    if (!exportRef.current || exporting) return;
    setExporting(true);
    try {
      const arrayBuffer = await renderSinglePagePdf(exportRef.current);
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      downloadBlob(blob, `drill-vs-match-targets-${dateFrom}_to_${dateTo}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('PDF export failed.');
    } finally {
      setExporting(false);
    }
  };

  const thStyle = { padding: '9px 10px', background: 'var(--bg-app)', color: 'var(--text-muted)', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: 'right', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' };
  const thLeft = { ...thStyle, textAlign: 'left' };
  const sortIndicator = key => sortKey === key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '';
  const td0 = { padding: '10px 10px', fontSize: 13, color: 'var(--text-primary)', textAlign: 'right', borderBottom: '1px solid var(--bg-app)' };
  const td0L = { ...td0, textAlign: 'left' };
  const green = { background: 'rgba(16,185,129,0.12)', color: '#6ee7b7' };
  const greenCell = { ...green, color: 'var(--text-on-green)' };
  const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12 };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        {onNavigate && (
          <button onClick={() => onNavigate('metrics-table')} title="Back to Metrics Table"
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 8, color: 'var(--text-muted)', cursor: 'pointer', padding: 8, display: 'flex' }}>
            <ArrowLeft size={16} />
          </button>
        )}
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Drill vs Match Targets</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>Select a training drill and compare each player's load to their match-day target, pro-rated for minutes played.</p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setDatesPickerOpen(o => !o)}
            style={{ ...inputStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            Session dates {selectedSessionDates.size ? `(${selectedSessionDates.size})` : '(range below)'} <ChevronDown size={14} />
          </button>
          {datesPickerOpen && (
            <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 20, background: 'var(--bg-app)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: 10, minWidth: 200, maxHeight: 320, overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
              <button onClick={() => setSelectedSessionDates(new Set())} style={{ fontSize: 12, background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', padding: '2px 0', marginBottom: 6 }}>Clear (use range below)</button>
              {sessionDateOptions.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>No sessions found.</div>}
              {sessionDateOptions.map(opt => (
                <label key={opt.date} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '4px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedSessionDates.has(opt.date)} onChange={() => toggleSessionDate(opt.date)} style={{ accentColor: '#3b82f6' }} />
                  {opt.label}
                </label>
              ))}
            </div>
          )}
        </div>
        <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>From</label>
        <input type="date" value={dateFrom} onChange={e => handleDateFromChange(e.target.value)} style={inputStyle} />
        <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>To</label>
        <input type="date" value={dateTo} onChange={e => handleDateToChange(e.target.value)} style={inputStyle} />
        <div style={{ position: 'relative' }}>
          <button onClick={() => setDrillPickerOpen(o => !o)}
            style={{ ...inputStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            Drills {selectedDrills.size ? `(${selectedDrills.size}${combineDrills ? ', combined' : ''})` : '(all)'} <ChevronDown size={14} />
          </button>
          {drillPickerOpen && (
            <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 20, background: 'var(--bg-app)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: 10, minWidth: 240, maxHeight: 300, overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
              <button onClick={() => setSelectedDrills(new Set())} style={{ fontSize: 12, background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', padding: '2px 0', marginBottom: 6 }}>Clear (show all)</button>
              {drillTitles.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>No drills in range.</div>}
              {drillTitles.map(title => (
                <label key={title} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '4px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedDrills.has(title)} onChange={() => toggleDrill(title)} style={{ accentColor: '#3b82f6' }} />
                  {title}
                </label>
              ))}
              {selectedDrills.size >= 2 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600, padding: '8px 0 2px', marginTop: 6, borderTop: '1px solid var(--border-strong)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={combineSelectedDrills} onChange={e => setCombineSelectedDrills(e.target.checked)} style={{ accentColor: '#3b82f6' }} />
                  Combine into one row per player
                </label>
              )}
            </div>
          )}
        </div>
        <select value={posFilter} onChange={e => setPosFilter(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
          {POSITIONS.map(pos => <option key={pos} value={pos}>{pos === 'All' ? 'All positions' : pos}</option>)}
        </select>
        <button onClick={handleExportPdf} disabled={exporting || sortedRows.length === 0}
          style={{ marginLeft: 'auto', padding: '9px 14px', borderRadius: 8, border: 'none', background: exporting || sortedRows.length === 0 ? 'var(--border)' : '#3b82f6', color: exporting || sortedRows.length === 0 ? 'var(--text-dimmed)' : '#fff', cursor: exporting || sortedRows.length === 0 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Download size={14} /> {exporting ? 'Exporting…' : 'PDF export'}
        </button>
      </div>

      {/* Target percentage slider */}
      <div style={{ ...card, padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Match target level</div>
        <input
          type="range" min={50} max={200} step={5} value={targetPct}
          onChange={e => setTargetPct(Number(e.target.value))}
          style={{ flex: 1, minWidth: 160, accentColor: '#3b82f6' }}
        />
        <div style={{ fontSize: 16, fontWeight: 800, color: targetPct > 100 ? '#f59e0b' : 'var(--text-primary)', minWidth: 52, textAlign: 'right' }}>{targetPct}%</div>
        <button onClick={() => setTargetPct(100)}
          style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 11.5, fontWeight: 600 }}>
          Reset to 100%
        </button>
      </div>

      {error && <div style={{ padding: 14, borderRadius: 8, background: '#451a03', color: '#fca5a5', fontSize: 13, marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh', color: 'var(--text-muted)', gap: 12 }}>
          <Activity size={22} strokeWidth={1.5} /><span>Loading…</span>
        </div>
      ) : (
        <div style={{ ...card, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
            <thead>
              <tr>
                <th style={thLeft} onClick={() => handleSort('date')}>Date{sortIndicator('date')}</th>
                <th style={thLeft} onClick={() => handleSort('drillTitle')}>Drill{sortIndicator('drillTitle')}</th>
                <th style={thLeft} onClick={() => handleSort('playerName')}>Player{sortIndicator('playerName')}</th>
                <th style={thStyle} onClick={() => handleSort('td')}>TD{sortIndicator('td')}</th>
                <th style={thStyle} onClick={() => handleSort('tdPct')}>TD%{sortIndicator('tdPct')}</th>
                <th style={thStyle} onClick={() => handleSort('hid')}>HID{sortIndicator('hid')}</th>
                <th style={thStyle} onClick={() => handleSort('hidPct')}>HID%{sortIndicator('hidPct')}</th>
                <th style={thStyle} onClick={() => handleSort('sd')}>SD{sortIndicator('sd')}</th>
                <th style={thStyle} onClick={() => handleSort('sdPct')}>SD%{sortIndicator('sdPct')}</th>
                <th style={thStyle} onClick={() => handleSort('hmld')}>HMLD{sortIndicator('hmld')}</th>
                <th style={thStyle} onClick={() => handleSort('hmldPct')}>HMLD%{sortIndicator('hmldPct')}</th>
                <th style={thStyle} onClick={() => handleSort('sa')}>SA{sortIndicator('sa')}</th>
                <th style={thStyle} onClick={() => handleSort('saPct')}>SA%{sortIndicator('saPct')}</th>
                <th style={thStyle} onClick={() => handleSort('maxSpeed')}>Max spd{sortIndicator('maxSpeed')}</th>
                <th style={thStyle} onClick={() => handleSort('mins')}>Mins{sortIndicator('mins')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr><td colSpan={14} style={{ ...td0, textAlign: 'center', color: 'var(--text-dimmed)', padding: 32 }}>
                  No drill data for this filter.
                </td></tr>
              ) : sortedRows.map(r => (
                <tr key={r.id}>
                  <td style={{ ...td0L, fontSize: 12, whiteSpace: 'nowrap' }}>{r.date}</td>
                  <td style={{ ...td0L, fontSize: 12, color: 'var(--text-muted)' }}>{r.drillTitle}</td>
                  <td style={td0L}>
                    <span style={{ fontWeight: 600 }}>{r.playerName}</span>
                    {r.position && <span style={{ fontSize: 11, color: 'var(--text-dimmed)', marginLeft: 6 }}>{r.position}</span>}
                  </td>
                  <td style={{ ...td0, ...(r.tdGreen ? greenCell : {}) }}>{f0(r.td)}</td>
                  <td style={{ ...td0, ...(r.tdGreen ? greenCell : {}) }}>{fPct(r.tdPct)}</td>
                  <td style={{ ...td0, ...(r.hidGreen ? greenCell : {}) }}>{f0(r.hid)}</td>
                  <td style={{ ...td0, ...(r.hidGreen ? greenCell : {}) }}>{fPct(r.hidPct)}</td>
                  <td style={{ ...td0, ...(r.sdGreen ? greenCell : {}) }}>{f0(r.sd)}</td>
                  <td style={{ ...td0, ...(r.sdGreen ? greenCell : {}) }}>{fPct(r.sdPct)}</td>
                  <td style={{ ...td0, ...(r.hmldGreen ? greenCell : {}) }}>{f0(r.hmld)}</td>
                  <td style={{ ...td0, ...(r.hmldGreen ? greenCell : {}) }}>{fPct(r.hmldPct)}</td>
                  <td style={{ ...td0, ...(r.saGreen ? greenCell : {}) }}>{f0(r.sa)}</td>
                  <td style={{ ...td0, ...(r.saGreen ? greenCell : {}) }}>{fPct(r.saPct)}</td>
                  <td style={td0}>{f1(r.maxSpeed)}</td>
                  <td style={td0}>{f1(r.mins)}</td>
                </tr>
              ))}
            </tbody>
            {sortedRows.length > 0 && (
              <tfoot>
                <tr style={{ background: 'var(--bg-subtle)' }}>
                  <td colSpan={3} style={{ ...td0L, fontWeight: 700, color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {posFilter === 'All' ? 'Total' : `${posFilter} total`}
                  </td>
                  <td style={{ ...td0, fontWeight: 700 }}>{f0(totals.td)}</td>
                  <td style={{ ...td0, fontWeight: 700 }}>{fPct(totals.tdPct)}</td>
                  <td style={{ ...td0, fontWeight: 700 }}>{f0(totals.hid)}</td>
                  <td style={{ ...td0, fontWeight: 700 }}>{fPct(totals.hidPct)}</td>
                  <td style={{ ...td0, fontWeight: 700 }}>{f0(totals.sd)}</td>
                  <td style={{ ...td0, fontWeight: 700 }}>{fPct(totals.sdPct)}</td>
                  <td style={{ ...td0, fontWeight: 700, color: '#f59e0b' }}>{f0(totals.hmld)}</td>
                  <td style={{ ...td0, fontWeight: 700 }}>{fPct(totals.hmldPct)}</td>
                  <td style={{ ...td0, fontWeight: 700 }}>{f0(totals.sa)}</td>
                  <td style={{ ...td0, fontWeight: 700 }}>{fPct(totals.saPct)}</td>
                  <td style={{ ...td0, fontWeight: 700 }}>{f1(totals.maxSpeed)}</td>
                  <td style={td0}>{f1(totals.mins)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* Hidden print-styled copy of the table above, captured for the PDF export — off-screen
          (not display:none, which html2canvas can't capture) but always kept in the DOM/current
          with live data so the export button never needs a re-render round trip before it can
          capture. Fixed light colors throughout (see the EX/exTh/exTd consts up top) rather than
          this page's dark-mode CSS variables. */}
      <div ref={exportRef} style={{ position: 'fixed', top: 0, left: -10000, width: 1400, background: EX.bg, padding: 20, fontFamily: 'Inter, sans-serif' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: EX.text, marginBottom: 2 }}>Drill vs Match Targets</div>
        <div style={{ fontSize: 10, color: EX.muted, marginBottom: 12 }}>{exportSubtitle}</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={exThLeft}>Date</th>
              <th style={exThLeft}>Drill</th>
              <th style={exThLeft}>Player</th>
              <th style={exTh}>TD</th>
              <th style={exTh}>TD%</th>
              <th style={exTh}>HID</th>
              <th style={exTh}>HID%</th>
              <th style={exTh}>SD</th>
              <th style={exTh}>SD%</th>
              <th style={exTh}>HMLD</th>
              <th style={exTh}>HMLD%</th>
              <th style={exTh}>SA</th>
              <th style={exTh}>SA%</th>
              <th style={exTh}>Max spd</th>
              <th style={exTh}>Mins</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(r => (
              <tr key={r.id}>
                <td style={exTdL}>{r.date}</td>
                <td style={exTdL}>{r.drillTitle}</td>
                <td style={exTdL}>{r.playerName}{r.position ? ` (${r.position})` : ''}</td>
                <td style={{ ...exTd, ...(r.tdGreen ? exGreen : {}) }}>{f0(r.td)}</td>
                <td style={{ ...exTd, ...(r.tdGreen ? exGreen : {}) }}>{fPct(r.tdPct)}</td>
                <td style={{ ...exTd, ...(r.hidGreen ? exGreen : {}) }}>{f0(r.hid)}</td>
                <td style={{ ...exTd, ...(r.hidGreen ? exGreen : {}) }}>{fPct(r.hidPct)}</td>
                <td style={{ ...exTd, ...(r.sdGreen ? exGreen : {}) }}>{f0(r.sd)}</td>
                <td style={{ ...exTd, ...(r.sdGreen ? exGreen : {}) }}>{fPct(r.sdPct)}</td>
                <td style={{ ...exTd, ...(r.hmldGreen ? exGreen : {}) }}>{f0(r.hmld)}</td>
                <td style={{ ...exTd, ...(r.hmldGreen ? exGreen : {}) }}>{fPct(r.hmldPct)}</td>
                <td style={{ ...exTd, ...(r.saGreen ? exGreen : {}) }}>{f0(r.sa)}</td>
                <td style={{ ...exTd, ...(r.saGreen ? exGreen : {}) }}>{fPct(r.saPct)}</td>
                <td style={exTd}>{f1(r.maxSpeed)}</td>
                <td style={exTd}>{f1(r.mins)}</td>
              </tr>
            ))}
          </tbody>
          {sortedRows.length > 0 && (
            <tfoot>
              <tr style={{ background: EX.subtle }}>
                <td colSpan={3} style={{ ...exTdL, fontWeight: 700 }}>{posFilter === 'All' ? 'Total' : `${posFilter} total`}</td>
                <td style={{ ...exTd, fontWeight: 700 }}>{f0(totals.td)}</td>
                <td style={{ ...exTd, fontWeight: 700 }}>{fPct(totals.tdPct)}</td>
                <td style={{ ...exTd, fontWeight: 700 }}>{f0(totals.hid)}</td>
                <td style={{ ...exTd, fontWeight: 700 }}>{fPct(totals.hidPct)}</td>
                <td style={{ ...exTd, fontWeight: 700 }}>{f0(totals.sd)}</td>
                <td style={{ ...exTd, fontWeight: 700 }}>{fPct(totals.sdPct)}</td>
                <td style={{ ...exTd, fontWeight: 700 }}>{f0(totals.hmld)}</td>
                <td style={{ ...exTd, fontWeight: 700 }}>{fPct(totals.hmldPct)}</td>
                <td style={{ ...exTd, fontWeight: 700 }}>{f0(totals.sa)}</td>
                <td style={{ ...exTd, fontWeight: 700 }}>{fPct(totals.saPct)}</td>
                <td style={{ ...exTd, fontWeight: 700 }}>{f1(totals.maxSpeed)}</td>
                <td style={exTd}>{f1(totals.mins)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
